import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { quotationSchema, type QuotationFormData } from '@/lib/validators';
import React from 'react';
import { useQuotations, useQuotation } from '@/composables/financial/useQuotations';
import { useContactOptions } from '@/composables/crm/useContactOptions';
import { useOrganizationSettings } from '@/composables/shared/useOrganizationSettings';
import { useSignatures } from '@/composables/shared/useSignatures';
import { formatCurrency, formatFullName } from '@/lib/formatters';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormDatePicker } from '@/components/forms/FormDatePicker';
import { DecimalInput } from '@/components/forms/DecimalInput';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Eye, Plus } from 'lucide-react';
import { usePdfActions, PdfAbort } from '@/composables/shared/usePdfActions';
import { quotationService } from '@/services/quotation.service';
import { LineItemsEditor } from '@/components/forms/LineItemsEditor';
import { GstinWarning } from '@/components/forms/GstinWarning';
import { cleanChildren, itemsToForm } from '@/lib/documentItems';
import { previewTaxLines, taxSupplyOptions } from '@/lib/tax';
import { CURRENCIES } from '@/lib/constants';

export function QuotationCreatePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const { createQuotation, updateQuotation } = useQuotations();
  // Pickers read the full contact list, never the filtered list store.
  const { contacts } = useContactOptions();
  const { settings: orgSettings } = useOrganizationSettings();
  const { signatures, defaultSignature } = useSignatures();
  const { quotation: existing, isLoading: loadingExisting } = useQuotation(id || '');
  const taxPrefillApplied = React.useRef(false);
  const signaturePrefillApplied = React.useRef(false);
  // Which contact the billing fields were last filled from — see the autofill
  // effect below.
  const autofilledContactId = React.useRef<string | null>(null);

  const form = useForm<QuotationFormData>({
    resolver: zodResolver(quotationSchema),
    defaultValues: {
      issueDate: '', validUntil: '', currency: 'USD', taxRate: 0, discountAmount: 0,
      pricingMode: 'ITEMIZED', subtotal: 0,
      notes: '', terms: '', contactId: '', taxId: '', signatureId: '',
      billingCompanyName: '',
      billingTaxId: '', taxSupply: 'AUTO',
      items: [{ description: '', quantity: 1, unitPrice: 0, taxRate: 0, children: [] }],
    },
  });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'items' });

  // Default validUntil to today + orgSettings.quotationDefaultValidityDays (only for new quotations)
  React.useEffect(() => {
    if (isEdit || !orgSettings) return;
    if (!form.getValues('issueDate')) {
      const today = new Date().toISOString().slice(0, 10);
      form.setValue('issueDate', today);
      const validity = orgSettings.quotationDefaultValidityDays || 30;
      const vu = new Date(Date.now() + validity * 86400000).toISOString().slice(0, 10);
      form.setValue('validUntil', vu);
    }
  }, [isEdit, orgSettings, form]);

  // Prefill tax ID from org settings (one-time, only if blank)
  React.useEffect(() => {
    if (!orgSettings || taxPrefillApplied.current) return;
    taxPrefillApplied.current = true;
    if (!form.getValues('taxId') && orgSettings.taxId) form.setValue('taxId', orgSettings.taxId);
  }, [orgSettings, form]);

  // Prefill default signature (only once, only for new quotations / blank value)
  React.useEffect(() => {
    if (signaturePrefillApplied.current || !defaultSignature) return;
    if (!form.getValues('signatureId')) {
      form.setValue('signatureId', defaultSignature.id);
      signaturePrefillApplied.current = true;
    }
  }, [defaultSignature, form]);

  // Hydrate form when editing an existing quotation
  React.useEffect(() => {
    if (!isEdit || !existing) return;
    form.reset({
      issueDate: (existing.issueDate || '').slice(0, 10),
      validUntil: (existing.validUntil || '').slice(0, 10),
      currency: (existing.currency as QuotationFormData['currency']) || 'USD',
      taxRate: Number(existing.taxRate) || 0,
      discountAmount: Number(existing.discountAmount) || 0,
      notes: existing.notes ?? '',
      terms: existing.terms ?? '',
      billingCompanyName: existing.billingCompanyName ?? '',
      billingName: existing.billingName ?? '',
      billingEmail: existing.billingEmail ?? '',
      billingAddress: existing.billingAddress ?? '',
      billingCity: existing.billingCity ?? '',
      billingState: existing.billingState ?? '',
      billingCountry: existing.billingCountry ?? '',
      billingPostalCode: existing.billingPostalCode ?? '',
      contactId: existing.contactId ?? '',
      taxId: existing.taxId ?? '',
      signatureId: existing.signatureId ?? '',
      pricingMode: existing.pricingMode ?? 'ITEMIZED',
      subtotal: Number(existing.subtotal) || 0,
      billingTaxId: existing.billingTaxId ?? '',
      taxSupply: existing.taxSupply ?? 'AUTO',
      items: itemsToForm(existing.items),
    });
    // Mark the hydrated contact as already applied, so the autofill effect
    // treats it as the document's own snapshot rather than a fresh pick.
    autofilledContactId.current = existing.contactId ?? '';
  }, [isEdit, existing, form]);

  // Fills only when the USER picks a contact. On an edit page the contacts
  // list resolves after form.reset(), so without the ref this fired on the
  // hydrated value and overwrote the quotation's billing snapshot with the
  // contact's current details. The ref moves only after a lookup succeeds, so
  // a pick made before the list loads still fills in once it arrives.
  const selectedContactId = form.watch('contactId');
  React.useEffect(() => {
    if (!selectedContactId || autofilledContactId.current === selectedContactId) return;
    const contact = contacts.find(c => c.id === selectedContactId);
    if (!contact) return;
    autofilledContactId.current = selectedContactId;
    form.setValue('billingName', `${contact.firstName} ${contact.lastName}`);
    if (contact.company?.name) form.setValue('billingCompanyName', contact.company.name);
    if (contact.email) form.setValue('billingEmail', contact.email);
    if (contact.address) form.setValue('billingAddress', contact.address);
    if (contact.city) form.setValue('billingCity', contact.city);
    if (contact.state) form.setValue('billingState', contact.state);
    if (contact.country) form.setValue('billingCountry', contact.country);
    if (contact.postalCode) form.setValue('billingPostalCode', contact.postalCode);
  }, [selectedContactId, contacts, form]);

  // Live totals
  const watchItems = form.watch('items');
  const watchTaxRate = form.watch('taxRate');
  const watchDiscount = form.watch('discountAmount');
  const pricingMode = form.watch('pricingMode');
  const watchSubtotal = form.watch('subtotal');
  const watchCurrency = form.watch('currency');
  const money = (v: number) => formatCurrency(v, watchCurrency);
  const lumpSum = pricingMode === 'LUMP_SUM';
  // Lump-sum: the typed subtotal is the truth. Itemized: derive it from the items.
  const subtotalLive = lumpSum
    ? Number(watchSubtotal) || 0
    : (watchItems || []).reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unitPrice) || 0), 0);
  const taxLive = subtotalLive * ((Number(watchTaxRate) || 0) / 100);
  const totalLive = Math.max(0, subtotalLive + taxLive - (Number(watchDiscount) || 0));
  // What the tax will print as. Offered whenever tax is charged (or a choice
  // is already stored): one line by default, CGST + SGST / IGST on request.
  const taxSupply = form.watch('taxSupply');
  const showTaxLines = Number(watchTaxRate) > 0 || taxSupply !== 'AUTO';
  const taxLinesLive = previewTaxLines(Number(watchTaxRate) || 0, taxLive, orgSettings, taxSupply, {
    billingState: form.watch('billingState'), billingCountry: form.watch('billingCountry'),
  });

  // Under lump-sum the items carry no money — the document subtotal does.
  const buildPayload = (data: QuotationFormData) => {
    const lump = data.pricingMode === 'LUMP_SUM';
    const items = data.items.map((item, i) => ({
      description: item.description,
      quantity: lump ? 0 : item.quantity,
      unitPrice: lump ? 0 : item.unitPrice,
      amount: lump ? 0 : item.quantity * item.unitPrice,
      taxRate: lump ? 0 : item.taxRate,
      position: i,
      children: cleanChildren(item.children),
    }));
    const subtotal = lump ? Number(data.subtotal) || 0 : items.reduce((sum, item) => sum + item.amount, 0);
    const taxAmount = subtotal * (data.taxRate / 100);
    const totalAmount = Math.max(0, subtotal + taxAmount - data.discountAmount);
    // AUTO is the form's word for "no override"; the API takes null.
    return { ...data, items, subtotal, taxAmount, totalAmount, taxSupply: data.taxSupply === 'AUTO' ? null : data.taxSupply };
  };

  // Preview the form as it stands — nothing is saved. The tab opens on the
  // click itself (so it isn't blocked), then validation runs.
  const pdf = usePdfActions();
  const handlePreview = () => pdf.preview(async () => {
    if (!(await form.trigger())) throw new PdfAbort('Fix the highlighted fields to preview.');
    return quotationService.preview(buildPayload(quotationSchema.parse(form.getValues())), isEdit ? id : undefined);
  }, 'quotation-preview.pdf');

  const onSubmit = (data: QuotationFormData) => {
    const payload = buildPayload(data);
    if (isEdit && id) {
      updateQuotation.mutate({ id, data: payload }, { onSuccess: () => navigate(`/financial/quotations/${id}`) });
    } else {
      createQuotation.mutate(payload, { onSuccess: () => navigate('/financial/quotations') });
    }
  };

  if (isEdit && loadingExisting) {
    return <PageContainer backTo title="Edit Quotation"><div className="text-muted-foreground text-sm">Loading…</div></PageContainer>;
  }

  const submitting = createQuotation.isPending || updateQuotation.isPending;

  return (
    <PageContainer backTo title={isEdit ? 'Edit Quotation' : 'Create Quotation'}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card><CardHeader><CardTitle className="text-base">Quotation Details</CardTitle></CardHeader><CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormDatePicker form={form} name="issueDate" label="Issue Date" required />
              <FormDatePicker form={form} name="validUntil" label="Valid Until" required />
            </div>
            <FormSelect form={form} name="currency" label="Currency" options={CURRENCIES.map(c => ({ label: c, value: c }))} />
            <div className="grid grid-cols-2 gap-4">
              <FormField form={form} name="taxRate" label="Tax Rate (%)" type="decimal" />
              <FormField form={form} name="discountAmount" label="Discount Amount" type="decimal" description="Deducted from total" />
            </div>
            <FormField form={form} name="taxId" label="Tax ID / GSTIN" description="Pre-filled from settings" />
            {showTaxLines && (
              <FormSelect form={form} name="taxSupply" label="Tax lines" options={taxSupplyOptions(orgSettings?.taxMode, orgSettings?.taxLabel)} />
            )}
            <GstinWarning taxRate={Number(watchTaxRate) || 0} docTaxId={form.watch('taxId')} orgTaxId={orgSettings?.taxId} />
            <FormSelect
              form={form}
              name="signatureId"
              label="Signature"
              options={signatures.map((s) => ({ label: `${s.label}${s.isDefault ? ' (Default)' : ''}`, value: s.id }))}
              placeholder={defaultSignature ? `Organisation default — ${defaultSignature.label}` : 'Select…'}
            />
          </CardContent></Card>
          <Card><CardHeader><CardTitle className="text-base">Quote To</CardTitle></CardHeader><CardContent className="space-y-4">
            <FormSelect form={form} name="contactId" label="Contact" options={contacts.map(c => ({ label: formatFullName(c.firstName, c.lastName), value: c.id }))} />
            <FormField form={form} name="billingCompanyName" label="Client Company" description="Shown on top of the QUOTE TO block (optional)" />
            <FormField form={form} name="billingName" label="Client Name" />
            <FormField form={form} name="billingEmail" label="Client Email" type="email" />
            <FormField form={form} name="billingAddress" label="Address" />
            <div className="grid grid-cols-2 gap-4">
              <FormField form={form} name="billingCity" label="City" />
              <FormField form={form} name="billingState" label="State" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField form={form} name="billingCountry" label="Country" />
              <FormField form={form} name="billingPostalCode" label="PIN / Postal Code" />
            </div>
            <FormField form={form} name="billingTaxId" label="Client GSTIN / Tax ID" description="The client's own registration, printed under their address" />
          </CardContent></Card>
        </div>

        <Card className="mt-6">
          <CardHeader className="space-y-3">
            <div className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Line Items</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={() => append({ description: '', quantity: 1, unitPrice: 0, taxRate: 0, children: [] })}><Plus className="mr-1 h-4 w-4" />Add Item</Button>
            </div>
            <RadioGroup
              value={pricingMode}
              onValueChange={(v) => form.setValue('pricingMode', v as 'ITEMIZED' | 'LUMP_SUM')}
              className="flex flex-wrap gap-x-6 gap-y-2"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="ITEMIZED" id="qpm-itemized" />
                <Label htmlFor="qpm-itemized" className="text-sm font-normal">Price each item</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="LUMP_SUM" id="qpm-lump" />
                <Label htmlFor="qpm-lump" className="text-sm font-normal">List items, one total</Label>
              </div>
            </RadioGroup>
            <CardDescription>
              {lumpSum
                ? 'Items are listed by description only. Qty and unit price are hidden on the quotation PDF.'
                : 'The subtotal is calculated from quantity × unit price.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <LineItemsEditor form={form} fields={fields} remove={remove} lumpSum={lumpSum} />
            {form.formState.errors.items && <p className="text-xs text-destructive">{form.formState.errors.items.message || form.formState.errors.items.root?.message}</p>}

            {lumpSum && (
              <div className="border-t pt-4 mt-4 ml-auto w-full max-w-xs">
                <Label htmlFor="q-lump-subtotal">Total for all items</Label>
                <DecimalInput id="q-lump-subtotal" {...form.register('subtotal')} />
                <p className="mt-1 text-xs text-muted-foreground">Before tax and discount.</p>
                {form.formState.errors.subtotal && <p className="mt-1 text-xs text-destructive">{form.formState.errors.subtotal.message}</p>}
              </div>
            )}

            <div className="border-t pt-4 mt-4 ml-auto w-full max-w-xs space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(subtotalLive)}</span></div>
              {taxLinesLive.map((line) => (
              <div key={line.label} className="flex justify-between"><span className="text-muted-foreground">{line.label}</span><span>{money(line.amount)}</span></div>
            ))}
              {Number(watchDiscount) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>- {money(Number(watchDiscount))}</span></div>}
              <div className="flex justify-between font-bold pt-1 border-t"><span>Total</span><span>{money(totalLive)}</span></div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6"><CardHeader><CardTitle className="text-base">Notes</CardTitle></CardHeader><CardContent className="space-y-4">
          <FormTextarea form={form} name="notes" label="Notes" /><FormTextarea form={form} name="terms" label="Terms & Conditions" />
        </CardContent></Card>

        <div className="mt-6 flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="button" variant="outline" onClick={handlePreview} loading={pdf.isPreviewing}><Eye className="mr-2 h-4 w-4" />Preview</Button>
          <Button type="submit" disabled={submitting}>{submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Quotation'}</Button>
        </div>
      </form>
    </PageContainer>
  );
}
